import {isPlatformBrowser, NgClass, NgForOf, NgIf} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    PLATFORM_ID,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {catchError, debounceTime, distinctUntilChanged, map, of, Subject, switchMap, takeUntil, tap} from 'rxjs';
import {GeoPlace, PopularRoute, RouteResult} from '../../../interfaces/route';
import {AnalyticsService} from '../../../services/analytics.service';
import {RouteService} from '../../../services/route.service';
import {RouteMapComponent} from '../route-map/route-map.component';

type RouteEndpoint = 'origin' | 'destination';
type RoutePlannerContext = 'distance' | 'trip' | 'fuel' | 'home';

interface PersistedRouteState {
    origin?: GeoPlace;
    destination?: GeoPlace;
    roundTrip?: boolean;
}

const STORAGE_KEY = 'motordesk.routePlanner.state.v1';

@Component({
    selector: 'app-route-planner',
    standalone: true,
    imports: [FormsModule, NgClass, NgForOf, NgIf, RouteMapComponent],
    templateUrl: './route-planner.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutePlannerComponent implements OnInit, OnDestroy {
    @Input() compact = false;
    @Input() context: RoutePlannerContext = 'distance';
    @Input() initialOrigin: GeoPlace | null = null;
    @Input() roundTrip = false;
    @Output() routeCalculated = new EventEmitter<RouteResult>();
    @Output() routeInvalidated = new EventEmitter<void>();
    @Output() roundTripChange = new EventEmitter<boolean>();

    originQuery = '';
    destinationQuery = '';
    origin: GeoPlace | null = null;
    destination: GeoPlace | null = null;
    originSuggestions: GeoPlace[] = [];
    destinationSuggestions: GeoPlace[] = [];
    popularRoutes: PopularRoute[] = [];
    loadingOrigin = false;
    loadingDestination = false;
    calculating = false;
    result: RouteResult | null = null;
    error = '';
    activeOriginIndex = -1;
    activeDestinationIndex = -1;

    private originInput$ = new Subject<string>();
    private destinationInput$ = new Subject<string>();
    private destroy$ = new Subject<void>();
    private calculationRevision = 0;

    constructor(
        private routes: RouteService,
        private analytics: AnalyticsService,
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    ngOnInit(): void {
        this.bindSearch('origin', this.originInput$);
        this.bindSearch('destination', this.destinationInput$);
        this.restoreState();

        if (this.initialOrigin) {
            this.setEndpoint('origin', this.initialOrigin);
        }

        if (!this.compact) {
            this.routes.popular(6).pipe(takeUntil(this.destroy$)).subscribe({
                next: ({items}) => {
                    this.popularRoutes = items;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => undefined,
            });
        }
        this.analytics.goalOnce('route_calculator_view', {context: this.context});

        if (this.canCalculate()) {
            this.calculate();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onQueryChange(endpoint: RouteEndpoint, value: string): void {
        this.invalidateCalculation();
        const query = value.trimStart();
        this.result = null;
        this.routeInvalidated.emit();
        this.error = '';

        if (endpoint === 'origin') {
            this.originQuery = query;
            this.origin = null;
            this.activeOriginIndex = -1;
            this.originInput$.next(query);
        } else {
            this.destinationQuery = query;
            this.destination = null;
            this.activeDestinationIndex = -1;
            this.destinationInput$.next(query);
        }
    }

    selectPlace(endpoint: RouteEndpoint, place: GeoPlace): void {
        this.invalidateCalculation();
        this.routeInvalidated.emit();
        this.setEndpoint(endpoint, place);
        this.error = '';
        this.persistState();
        this.analytics.goal('route_city_selected', {context: this.context, endpoint});
        if (this.canCalculate()) {
            this.calculate();
        }
    }

    onKeydown(endpoint: RouteEndpoint, event: KeyboardEvent): void {
        const suggestions = this.suggestions(endpoint);
        let activeIndex = this.activeIndex(endpoint);

        if (event.key === 'ArrowDown' && suggestions.length) {
            event.preventDefault();
            activeIndex = Math.min(activeIndex + 1, suggestions.length - 1);
            this.setActiveIndex(endpoint, activeIndex);
        } else if (event.key === 'ArrowUp' && suggestions.length) {
            event.preventDefault();
            activeIndex = Math.max(activeIndex - 1, 0);
            this.setActiveIndex(endpoint, activeIndex);
        } else if (event.key === 'Enter' && activeIndex >= 0 && suggestions[activeIndex]) {
            event.preventDefault();
            this.selectPlace(endpoint, suggestions[activeIndex]);
        } else if (event.key === 'Escape') {
            this.clearSuggestions(endpoint);
        }
    }

    closeSuggestions(endpoint: RouteEndpoint): void {
        window.setTimeout(() => this.clearSuggestions(endpoint), 120);
    }

    swapEndpoints(): void {
        this.invalidateCalculation();
        const previousOrigin = this.origin;
        const previousOriginQuery = this.originQuery;
        this.origin = this.destination;
        this.originQuery = this.destinationQuery;
        this.destination = previousOrigin;
        this.destinationQuery = previousOriginQuery;
        this.clearSuggestions('origin');
        this.clearSuggestions('destination');
        this.persistState();
        this.analytics.goal('route_swapped', {context: this.context});
        if (this.canCalculate()) {
            this.calculate();
        }
    }

    onRoundTripChange(value: boolean): void {
        this.invalidateCalculation();
        this.roundTrip = value;
        if (this.result) {
            const multiplier = value ? 2 : 1;
            this.result = {
                ...this.result,
                round_trip: value,
                effective_distance_km: this.result.distance_km * multiplier,
                effective_duration_minutes: this.result.duration_minutes * multiplier,
            };
        }
        this.roundTripChange.emit(value);
        this.persistState();
        if (value) {
            this.analytics.goal('route_round_trip_enabled', {context: this.context});
        }
        if (this.canCalculate()) {
            this.calculate();
        }
    }

    calculate(): void {
        if (!this.origin || !this.destination) {
            this.error = 'Выберите оба города из выпадающих подсказок.';
            this.changeDetectorRef.markForCheck();
            return;
        }
        if (this.origin.id === this.destination.id) {
            this.error = 'Город отправления и назначения должны отличаться.';
            this.changeDetectorRef.markForCheck();
            return;
        }

        const revision = ++this.calculationRevision;
        this.calculating = true;
        this.error = '';
        this.changeDetectorRef.markForCheck();
        this.routes.calculate(this.origin.id, this.destination.id, this.roundTrip, this.context)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    if (revision !== this.calculationRevision) {
                        return;
                    }
                    this.result = result;
                    this.calculating = false;
                    this.persistState();
                    this.routeCalculated.emit(result);
                    this.analytics.goal('route_calculated', {
                        context: this.context,
                        round_trip: result.round_trip,
                        cached: result.cached,
                    });
                    this.changeDetectorRef.markForCheck();
                },
                error: (response) => {
                    if (revision !== this.calculationRevision) {
                        return;
                    }
                    this.error = response?.error?.message || 'Не удалось построить маршрут. Попробуйте ещё раз.';
                    this.calculating = false;
                    this.analytics.goal('route_calculation_error', {context: this.context});
                    this.changeDetectorRef.markForCheck();
                },
            });
    }

    reset(): void {
        this.invalidateCalculation();
        this.origin = this.initialOrigin;
        this.originQuery = this.initialOrigin?.label || '';
        this.destination = null;
        this.destinationQuery = '';
        this.originSuggestions = [];
        this.destinationSuggestions = [];
        this.activeOriginIndex = -1;
        this.activeDestinationIndex = -1;
        this.loadingOrigin = false;
        this.loadingDestination = false;
        this.result = null;
        this.routeInvalidated.emit();
        this.error = '';

        if (this.roundTrip) {
            this.roundTrip = false;
            this.roundTripChange.emit(false);
        }

        this.clearPersistedState();
        this.analytics.goal('route_reset', {context: this.context});
        this.changeDetectorRef.markForCheck();
    }

    choosePopular(route: PopularRoute): void {
        this.originQuery = this.placeCaption(route.origin, route.origin_region);
        this.destinationQuery = this.placeCaption(route.destination, route.destination_region);
        this.origin = null;
        this.destination = null;
        this.originInput$.next(route.origin);
        this.destinationInput$.next(route.destination);
        this.error = 'Выберите подходящие города в подсказках, чтобы точно определить маршрут.';
        this.changeDetectorRef.markForCheck();
    }

    hasSuggestions(endpoint: RouteEndpoint): boolean {
        return this.suggestions(endpoint).length > 0;
    }

    isLoading(endpoint: RouteEndpoint): boolean {
        return endpoint === 'origin' ? this.loadingOrigin : this.loadingDestination;
    }

    optionId(endpoint: RouteEndpoint, index: number): string {
        return `route-${this.context}-${endpoint}-option-${index}`;
    }

    activeOptionId(endpoint: RouteEndpoint): string | null {
        const index = this.activeIndex(endpoint);
        return index >= 0 ? this.optionId(endpoint, index) : null;
    }

    durationLabel(minutes: number): string {
        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        if (!hours) {
            return `${rest} мин`;
        }
        return rest ? `${hours} ч ${rest} мин` : `${hours} ч`;
    }

    formatDistance(value: number): string {
        return new Intl.NumberFormat('ru-RU', {maximumFractionDigits: 1}).format(value);
    }

    canCalculate(): boolean {
        return !!this.origin && !!this.destination && this.origin.id !== this.destination.id;
    }

    canReset(): boolean {
        return !!this.originQuery.trim() || !!this.destinationQuery.trim() || !!this.result || this.roundTrip;
    }

    private bindSearch(endpoint: RouteEndpoint, input$: Subject<string>): void {
        input$.pipe(
            map((query) => query.trim()),
            debounceTime(280),
            distinctUntilChanged(),
            tap((query) => {
                this.setLoading(endpoint, query.length >= 2);
                if (query.length < 2) {
                    this.clearSuggestions(endpoint);
                }
            }),
            switchMap((query) => query.length < 2
                ? of([] as GeoPlace[])
                : this.routes.searchPlaces(query).pipe(
                    map(({items}) => items),
                    catchError(() => {
                        this.error = 'Подсказки городов временно недоступны. Повторите ввод чуть позже.';
                        return of([] as GeoPlace[]);
                    }),
                )),
            takeUntil(this.destroy$),
        ).subscribe((items) => {
            if (endpoint === 'origin') {
                this.originSuggestions = items;
            } else {
                this.destinationSuggestions = items;
            }
            this.setLoading(endpoint, false);
            this.changeDetectorRef.markForCheck();
        });
    }

    private setEndpoint(endpoint: RouteEndpoint, place: GeoPlace): void {
        if (endpoint === 'origin') {
            this.origin = place;
            this.originQuery = place.label;
        } else {
            this.destination = place;
            this.destinationQuery = place.label;
        }
        this.clearSuggestions(endpoint);
        this.changeDetectorRef.markForCheck();
    }

    private suggestions(endpoint: RouteEndpoint): GeoPlace[] {
        return endpoint === 'origin' ? this.originSuggestions : this.destinationSuggestions;
    }

    private activeIndex(endpoint: RouteEndpoint): number {
        return endpoint === 'origin' ? this.activeOriginIndex : this.activeDestinationIndex;
    }

    private setActiveIndex(endpoint: RouteEndpoint, index: number): void {
        if (endpoint === 'origin') {
            this.activeOriginIndex = index;
        } else {
            this.activeDestinationIndex = index;
        }
    }

    private clearSuggestions(endpoint: RouteEndpoint): void {
        if (endpoint === 'origin') {
            this.originSuggestions = [];
            this.activeOriginIndex = -1;
            this.loadingOrigin = false;
        } else {
            this.destinationSuggestions = [];
            this.activeDestinationIndex = -1;
            this.loadingDestination = false;
        }
        this.changeDetectorRef.markForCheck();
    }

    private setLoading(endpoint: RouteEndpoint, value: boolean): void {
        if (endpoint === 'origin') {
            this.loadingOrigin = value;
        } else {
            this.loadingDestination = value;
        }
        this.changeDetectorRef.markForCheck();
    }

    private placeCaption(name: string, region?: string | null): string {
        return region ? `${name}, ${region}` : name;
    }

    private persistState(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        try {
            const state: PersistedRouteState = {
                origin: this.origin || undefined,
                destination: this.destination || undefined,
                roundTrip: this.roundTrip,
            };
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
        }
    }

    private clearPersistedState(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        try {
            window.localStorage.removeItem(STORAGE_KEY);
        } catch {
        }
    }

    private invalidateCalculation(): void {
        this.calculationRevision++;
        this.calculating = false;
    }

    private restoreState(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return;
            }
            const state = JSON.parse(raw) as PersistedRouteState;
            if (state.origin?.id) {
                this.setEndpoint('origin', state.origin);
            }
            if (state.destination?.id) {
                this.setEndpoint('destination', state.destination);
            }
            if (this.context === 'distance') {
                this.roundTrip = !!state.roundTrip;
            }
        } catch {
        }
    }
}
